import { ref, computed } from 'vue';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import type { FlightPoint, Flight } from '@/types/flight';
import type { BackgroundGeolocationPlugin } from '@capacitor-community/background-geolocation';
import { BackgroundLocationPermissions } from '@/plugins/BackgroundLocationPermissions';
import {
  BACKGROUND_LOCATION_DENIED_MESSAGE,
  ensureForegroundLocationPermission,
  LOCATION_DENIED_MESSAGE,
} from '@/lib/locationPermissions';
import {
  appendRecordingDraftPoints,
  clearRecordingDraft,
  createRecordingDraft,
  loadRecordingDraft,
  type RecordingDraftMeta,
} from '@/services/recordingDraftStorage';
import { createRecordingClock } from '@/lib/recordingClock';
import {
  gpsHealthFromFixAge,
  shouldRestartGpsWatcher,
  type GpsHealth,
} from '@/lib/gpsHealth';
import {
  recorderBreadcrumb,
  recorderLog,
  reportGpsStall,
  reportGpsRecovered,
} from '@/lib/recorderTelemetry';
import { safeClearGeolocationWatch } from '@/lib/safeClearGeolocationWatch';
import {
  createAutoRecordDetector,
  type AutoStopReason,
  type MotionSample,
} from '@/lib/autoRecordDetector';
import { getAutoRecordConfig } from '@/composables/useAutoRecord';

const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>('BackgroundGeolocation');

const GPS_UPDATE_INTERVAL_MS = 1000;
const TRACK_HEADING_MIN_SPEED_KMH = 5;
const TRACK_HEADING_SMOOTHING = 0.35;
const CHECKPOINT_INTERVAL_MS = 30_000;
const TICKER_INTERVAL_MS = 1000;
const isNative = () => Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios';

// ── Singleton recorder state (survives tab navigation / component remount) ──
const isRecording = ref(false);
const isPaused = ref(false);
const points = ref<FlightPoint[]>([]);
const currentPosition = ref<FlightPoint | null>(null);
const currentGpsAccuracyM = ref<number | null>(null);
const currentSpeedKmh = ref<number | null>(null);
const currentTrackHeadingDeg = ref<number | null>(null);
const error = ref<string | null>(null);
const pendingDraft = ref<RecordingDraftMeta | null>(null);
const nowMs = ref(Date.now());
const lastFixAt = ref<number | null>(null);
/**
 * Set when motion detection decides the flight is over. The recorder cannot
 * persist a flight itself, so the Record page watches this, stops and saves.
 */
const autoStopRequest = ref<{ reason: AutoStopReason; at: number } | null>(null);
const autoStartPending = ref(false);

const autoDetector = createAutoRecordDetector(getAutoRecordConfig);

let watchId: string | null = null;
let checkpointTimerId: ReturnType<typeof setInterval> | null = null;
let backgroundWatcherId: string | null = null;
let recordingFlightId: string | null = null;
let draftMeta: RecordingDraftMeta | null = null;
let lastFlushedIndex = 0;
let runningMaxAltitude = 0;
let runningTotalDistanceKm = 0;
const clock = createRecordingClock();
let tickerId: ReturnType<typeof setInterval> | null = null;
let lastWatcherRestartAt = 0;
let watchdogRestartInFlight = false;
let stallEpisodeActive = false;

const duration = computed(() => {
  if (!clock.isRunning()) return 0;
  return clock.durationMs(nowMs.value);
});

const gpsFixAgeMs = computed(() => {
  if (lastFixAt.value == null) return null;
  return Math.max(0, nowMs.value - lastFixAt.value);
});

const gpsFixAgeSec = computed(() =>
  gpsFixAgeMs.value == null ? null : Math.round(gpsFixAgeMs.value / 1000),
);

const gpsHealth = computed<GpsHealth>(() => {
  if (!isRecording.value || isPaused.value) return 'ok';
  return gpsHealthFromFixAge(gpsFixAgeMs.value);
});

const maxAltitude = computed(() => runningMaxAltitude);
const totalDistance = computed(() => runningTotalDistanceKm);

export function isRecordingActive(): boolean {
  return isRecording.value;
}

export function isRecordingPaused(): boolean {
  return isPaused.value;
}

/** Flush pending GPS points to disk (e.g. when app moves to background). */
export function flushRecordingCheckpoint(): void {
  void flushCheckpoint();
}

/**
 * Feed a fix taken while *not* recording (the Record page's idle GPS watch).
 * Returns true when the configured start trigger has fired.
 */
export function observeIdleMotion(sample: MotionSample): boolean {
  if (isRecording.value) return false;
  const decision = autoDetector.observeIdle(sample);
  autoStartPending.value = autoDetector.isStartPending();
  return decision === 'start';
}

/** Clear a handled auto-stop request. */
export function clearAutoStopRequest(): void {
  autoStopRequest.value = null;
}

export function useFlightRecorder() {
  return {
    isRecording,
    points,
    currentPosition,
    currentGpsAccuracyM,
    currentSpeedKmh,
    currentTrackHeadingDeg,
    duration,
    gpsHealth,
    gpsFixAgeSec,
    maxAltitude,
    totalDistance,
    error,
    pendingDraft,
    isPaused,
    autoStopRequest,
    autoStartPending,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    detectInterruptedDraft,
    resumeFromDraft,
    finalizeDraft,
    discardDraft,
  };
}

function generateFlightId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

function recordMaxAltitude(current: number, altitude: number | null): number {
  if (altitude == null || !Number.isFinite(altitude)) {
    return current;
  }

  return Math.max(current, altitude);
}

function resetRunningStats(trackPoints: FlightPoint[]): void {
  runningMaxAltitude = 0;
  runningTotalDistanceKm = 0;
  for (let i = 0; i < trackPoints.length; i++) {
    runningMaxAltitude = recordMaxAltitude(runningMaxAltitude, trackPoints[i].altitude);
    if (i > 0) {
      runningTotalDistanceKm += haversineDistance(
        trackPoints[i - 1].latitude,
        trackPoints[i - 1].longitude,
        trackPoints[i].latitude,
        trackPoints[i].longitude,
      );
    }
  }
}

function applyPointStats(point: FlightPoint, prevPoint: FlightPoint | null): void {
  runningMaxAltitude = recordMaxAltitude(runningMaxAltitude, point.altitude);
  if (prevPoint) {
    runningTotalDistanceKm += haversineDistance(
      prevPoint.latitude,
      prevPoint.longitude,
      point.latitude,
      point.longitude,
    );
  }
}

export function computeInstantSpeedKmh(
  nextPoint: Pick<FlightPoint, 'latitude' | 'longitude' | 'timestamp'>,
  prevPoint: Pick<FlightPoint, 'latitude' | 'longitude' | 'timestamp'> | null,
  gpsSpeedMps?: number | null,
): number | null {
  let rawSpeedKmh: number | null = null;

  if (typeof gpsSpeedMps === 'number' && Number.isFinite(gpsSpeedMps) && gpsSpeedMps >= 0) {
    rawSpeedKmh = gpsSpeedMps * 3.6;
  } else if (prevPoint) {
    const deltaHours = (nextPoint.timestamp - prevPoint.timestamp) / (1000 * 60 * 60);
    if (deltaHours > 0) {
      const segmentKm = haversineDistance(
        prevPoint.latitude,
        prevPoint.longitude,
        nextPoint.latitude,
        nextPoint.longitude,
      );
      rawSpeedKmh = segmentKm / deltaHours;
    }
  }

  if (rawSpeedKmh == null || !Number.isFinite(rawSpeedKmh)) return null;
  return Math.max(0, Math.min(rawSpeedKmh, 500));
}

function updateCurrentSpeed(nextPoint: FlightPoint, gpsSpeedMps?: number | null) {
  const prevPoint = points.value.length > 0 ? points.value[points.value.length - 1] : null;
  const speedKmh = computeInstantSpeedKmh(nextPoint, prevPoint, gpsSpeedMps);
  if (speedKmh != null) {
    currentSpeedKmh.value = speedKmh;
  }
}

function updateTrackHeading(nextPoint: FlightPoint) {
  const prevPoint = points.value[points.value.length - 1];
  if (!prevPoint) return;

  const speedKmh = currentSpeedKmh.value;
  if (speedKmh == null || speedKmh < TRACK_HEADING_MIN_SPEED_KMH) {
    currentTrackHeadingDeg.value = null;
    return;
  }

  const rawHeading = calculateBearing(
    prevPoint.latitude,
    prevPoint.longitude,
    nextPoint.latitude,
    nextPoint.longitude,
  );

  const currentHeading = currentTrackHeadingDeg.value;
  if (currentHeading == null) {
    currentTrackHeadingDeg.value = rawHeading;
    return;
  }

  const diff = shortestAngleDiffDegrees(currentHeading, rawHeading);
  currentTrackHeadingDeg.value = normalizeDegrees(currentHeading + (diff * TRACK_HEADING_SMOOTHING));
}

function addPoint(point: FlightPoint, gpsSpeedMps?: number | null) {
  if (stallEpisodeActive) {
    stallEpisodeActive = false;
    reportGpsRecovered({
      stallDurationMs: lastFixAt.value == null ? 0 : Math.max(0, Date.now() - lastFixAt.value),
      pointCount: points.value.length,
    });
  }
  lastFixAt.value = Date.now();
  const prevPoint = points.value.length > 0 ? points.value[points.value.length - 1] : null;
  updateCurrentSpeed(point, gpsSpeedMps);
  updateTrackHeading(point);
  applyPointStats(point, prevPoint);
  points.value.push(point);
  currentPosition.value = point;
  observeRecordedMotion(point);
}

/** Run auto-stop detection over a freshly recorded point. */
function observeRecordedMotion(point: FlightPoint): void {
  if (!isRecording.value || isPaused.value || autoStopRequest.value) return;

  const reason = autoDetector.observeRecording({
    timestampMs: point.timestamp,
    speedKmh: currentSpeedKmh.value,
    altitudeM: point.altitude,
    accuracyM: currentGpsAccuracyM.value,
  });
  if (!reason) return;

  autoStopRequest.value = { reason, at: Date.now() };
  recorderLog('auto-stop requested', { reason, pointCount: points.value.length });
}

function onPosition(position: { coords: { latitude: number; longitude: number; altitude?: number | null; speed?: number | null; accuracy?: number | null }; timestamp: number } | null) {
  if (!position || !isRecording.value || isPaused.value) return;
  if (typeof position.coords.accuracy === 'number' && Number.isFinite(position.coords.accuracy)) {
    currentGpsAccuracyM.value = position.coords.accuracy;
  }
  addPoint({
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    altitude: position.coords.altitude ?? null,
    timestamp: position.timestamp,
  }, position.coords.speed ?? null);
}

function onPositionFromBackground(location: { latitude: number; longitude: number; altitude: number | null; time: number | null; speed?: number | null }) {
  if (!isRecording.value || isPaused.value) return;
  addPoint({
    latitude: location.latitude,
    longitude: location.longitude,
    altitude: location.altitude,
    timestamp: location.time ?? Date.now(),
  }, location.speed ?? null);
}

async function flushCheckpoint(): Promise<void> {
  if (!draftMeta || !isRecording.value) return;

  const newPoints = points.value.slice(lastFlushedIndex);
  if (newPoints.length === 0) return;

  try {
    draftMeta = await appendRecordingDraftPoints(draftMeta, newPoints);
    lastFlushedIndex = points.value.length;
  } catch (e) {
    console.warn('[recorder] Failed to checkpoint draft:', e);
  }
}

function startCheckpointTimer() {
  stopCheckpointTimer();
  checkpointTimerId = setInterval(() => {
    void flushCheckpoint();
  }, CHECKPOINT_INTERVAL_MS);
}

function stopCheckpointTimer() {
  if (checkpointTimerId !== null) {
    clearInterval(checkpointTimerId);
    checkpointTimerId = null;
  }
}

function startTicker() {
  stopTicker();
  nowMs.value = Date.now();
  tickerId = setInterval(() => {
    nowMs.value = Date.now();
    void maybeRestartGpsWatchers(nowMs.value);
  }, TICKER_INTERVAL_MS);
}

function stopTicker() {
  if (tickerId !== null) {
    clearInterval(tickerId);
    tickerId = null;
  }
}

async function maybeRestartGpsWatchers(now: number): Promise<void> {
  if (
    !shouldRestartGpsWatcher({
      nowMs: now,
      lastFixAtMs: lastFixAt.value,
      lastRestartAtMs: lastWatcherRestartAt,
      isRecording: isRecording.value,
      isPaused: isPaused.value,
      restartInFlight: watchdogRestartInFlight,
    })
  ) {
    return;
  }

  watchdogRestartInFlight = true;
  lastWatcherRestartAt = now;

  const fixAgeMs = gpsFixAgeMs.value;
  const pointCount = points.value.length;

  if (!stallEpisodeActive) {
    stallEpisodeActive = true;
    reportGpsStall({ fixAgeMs: fixAgeMs ?? 0, pointCount });
  }
  recorderBreadcrumb('watchdog restarting GPS watcher', {
    fixAgeMs,
    pointCount,
  });

  try {
    await stopGpsWatchers();
    if (!isRecording.value || isPaused.value) {
      return;
    }
    await startGpsWatchers();
  } finally {
    watchdogRestartInFlight = false;
  }
}

async function stopGpsWatchers() {
  if (watchId !== null) {
    const id = watchId;
    watchId = null;
    await safeClearGeolocationWatch(id);
  }
  if (backgroundWatcherId !== null) {
    const id = backgroundWatcherId;
    backgroundWatcherId = null;
    try {
      await BackgroundGeolocation.removeWatcher({ id });
    } catch (error) {
      // Plugin may already have dropped the watcher after permission loss / app lifecycle.
      console.warn('BackgroundGeolocation.removeWatcher failed:', error);
    }
  }
}

async function abortRecordingDueToPermission(message: string): Promise<void> {
  isRecording.value = false;
  isPaused.value = false;
  currentSpeedKmh.value = null;
  currentTrackHeadingDeg.value = null;
  stopCheckpointTimer();
  stopTicker();
  clock.stop();
  autoDetector.endRecording(Date.now());
  autoStartPending.value = false;
  autoStopRequest.value = null;
  lastFixAt.value = null;
  stallEpisodeActive = false;
  lastWatcherRestartAt = 0;
  watchdogRestartInFlight = false;
  await stopGpsWatchers();
  recordingFlightId = null;
  draftMeta = null;
  lastFlushedIndex = 0;
  points.value = [];
  currentPosition.value = null;
  runningMaxAltitude = 0;
  runningTotalDistanceKm = 0;
  await clearRecordingDraft();
  error.value = message;
  recorderLog('recording aborted: permission', { message });
}

async function requestRecordingPermissions(): Promise<boolean> {
  const foreground = await ensureForegroundLocationPermission();
  if (!foreground.granted) {
    error.value = foreground.error ?? LOCATION_DENIED_MESSAGE;
    return false;
  }

  if (isNative() && Capacitor.getPlatform() === 'android') {
    try {
      const notif = await BackgroundLocationPermissions.requestNotificationPermission();
      if (!notif.success && notif.status === 'denied') {
        console.warn('Notification permission denied — recording may stop when screen is off.');
      }
      const backgroundLoc = await BackgroundLocationPermissions.requestBackgroundLocationPermission();
      if (!backgroundLoc.success && backgroundLoc.status === 'denied') {
        error.value = BACKGROUND_LOCATION_DENIED_MESSAGE;
        return false;
      }
    } catch (e) {
      console.warn('Background permission request failed:', e);
      error.value = BACKGROUND_LOCATION_DENIED_MESSAGE;
      return false;
    }
  }

  return true;
}

async function startGpsWatchers(): Promise<boolean> {
  const gpsOptions = {
    enableHighAccuracy: true,
    timeout: 5000,
    maximumAge: 0,
  };

  try {
    recorderBreadcrumb('starting GPS watcher', { native: isNative() });
    if (isNative()) {
      backgroundWatcherId = await BackgroundGeolocation.addWatcher(
        {
          backgroundMessage: 'Recording your flight. Tap to return to the app.',
          backgroundTitle: 'Flight Track — Recording',
          requestPermissions: false,
          stale: false,
          distanceFilter: 0,
        },
        (location, err) => {
          if (err) {
            if (err.code === 'NOT_AUTHORIZED') {
              void abortRecordingDueToPermission(LOCATION_DENIED_MESSAGE);
            }
            console.warn('BackgroundGeolocation error:', err);
            recorderBreadcrumb('background GPS watcher error', { code: err.code });
            return;
          }
          if (location) onPositionFromBackground(location);
        },
      );
    } else {
      watchId = await Geolocation.watchPosition(
        {
          ...gpsOptions,
          minimumUpdateInterval: GPS_UPDATE_INTERVAL_MS,
        },
        (position, err) => {
          if (err) {
            console.warn('GPS watch error:', err);
            recorderBreadcrumb('GPS watch error', { message: String(err) });
            return;
          }
          onPosition(position ?? null);
        },
      );
    }
    if (lastFixAt.value == null) {
      lastFixAt.value = Date.now();
    }
    return true;
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : String(e);
    error.value = `Failed to start GPS tracking: ${message}`;
    await stopGpsWatchers();
    return false;
  }
}

async function startRecording() {
  if (isRecording.value) return;

  error.value = null;
  currentSpeedKmh.value = null;
  currentTrackHeadingDeg.value = null;

  if (!(await requestRecordingPermissions())) return;

  recordingFlightId = generateFlightId();
  const startTime = Date.now();
  points.value = [];
  currentPosition.value = null;
  lastFlushedIndex = 0;
  runningMaxAltitude = 0;
  runningTotalDistanceKm = 0;
  pendingDraft.value = null;
  lastFixAt.value = null;
  stallEpisodeActive = false;
  watchdogRestartInFlight = false;

  try {
    await createRecordingDraft(recordingFlightId, startTime);
    draftMeta = {
      id: recordingFlightId,
      startTime,
      pointCount: 0,
      lastCheckpointAt: startTime,
    };
  } catch (e) {
    console.warn('[recorder] Failed to create draft:', e);
    error.value = 'Could not start recording — storage unavailable.';
    recordingFlightId = null;
    draftMeta = null;
    return;
  }

  isRecording.value = true;
  isPaused.value = false;

  if (!(await startGpsWatchers())) {
    isRecording.value = false;
    isPaused.value = false;
    recordingFlightId = null;
    draftMeta = null;
    await clearRecordingDraft();
    return;
  }

  startCheckpointTimer();
  clock.start(startTime);
  startTicker();
  autoDetector.beginRecording(startTime);
  autoStartPending.value = false;
  recorderLog('recording started', { flightId: recordingFlightId });
}

function buildFlightFromPoints(flightId: string, durationMs: number): Flight {
  const now = new Date().toISOString();
  return {
    id: flightId,
    name: `Flight ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
    startTime: points.value[0].timestamp,
    endTime: points.value[points.value.length - 1].timestamp,
    points: [...points.value],
    distance: runningTotalDistanceKm,
    maxAltitude: runningMaxAltitude,
    duration: durationMs,
    syncStatus: 'local',
    createdAt: now,
    updatedAt: now,
  };
}

function pointsDurationMs(): number {
  if (points.value.length < 2) return 0;
  return points.value[points.value.length - 1].timestamp - points.value[0].timestamp;
}

async function pauseRecording(): Promise<void> {
  if (!isRecording.value || isPaused.value) return;

  isPaused.value = true;
  clock.pause(Date.now());
  recorderLog('recording paused');
  currentSpeedKmh.value = null;
  currentTrackHeadingDeg.value = null;
  stopCheckpointTimer();
  await stopGpsWatchers();
  await flushCheckpoint();
}

async function resumeRecording(): Promise<boolean> {
  if (!isRecording.value || !isPaused.value) return false;

  if (!(await requestRecordingPermissions())) return false;
  if (!(await startGpsWatchers())) return false;

  isPaused.value = false;
  clock.resume(Date.now());
  lastFixAt.value = Date.now();
  recorderLog('recording resumed');
  startCheckpointTimer();
  return true;
}

async function stopRecording(): Promise<Flight | null> {
  const finalDurationMs = clock.isRunning()
    ? clock.durationMs(Date.now())
    : pointsDurationMs();

  isRecording.value = false;
  isPaused.value = false;
  currentSpeedKmh.value = null;
  currentTrackHeadingDeg.value = null;
  stopCheckpointTimer();
  stopTicker();
  clock.stop();
  autoDetector.endRecording(Date.now());
  autoStartPending.value = false;
  autoStopRequest.value = null;
  recorderLog('recording stopped', { pointCount: points.value.length });
  await stopGpsWatchers();
  await flushCheckpoint();

  const flightId = recordingFlightId;
  recordingFlightId = null;
  draftMeta = null;
  lastFlushedIndex = 0;
  lastFixAt.value = null;
  stallEpisodeActive = false;
  lastWatcherRestartAt = 0;
  watchdogRestartInFlight = false;

  if (points.value.length < 2) {
    error.value = 'Not enough GPS points recorded. Try recording for longer.';
    await clearRecordingDraft();
    pendingDraft.value = null;
    return null;
  }

  const flight = buildFlightFromPoints(flightId ?? generateFlightId(), finalDurationMs);
  points.value = [];
  currentPosition.value = null;
  runningMaxAltitude = 0;
  runningTotalDistanceKm = 0;
  await clearRecordingDraft();
  pendingDraft.value = null;
  return flight;
}

async function detectInterruptedDraft(): Promise<void> {
  if (isRecording.value || pendingDraft.value) return;

  const draft = await loadRecordingDraft();
  if (!draft || draft.points.length === 0) {
    if (draft) await clearRecordingDraft();
    return;
  }

  pendingDraft.value = draft.meta;
}

async function resumeFromDraft(): Promise<boolean> {
  if (isRecording.value) return false;

  const draft = await loadRecordingDraft();
  if (!draft || draft.points.length === 0) {
    pendingDraft.value = null;
    return false;
  }

  if (!(await requestRecordingPermissions())) return false;

  recordingFlightId = draft.meta.id;
  draftMeta = { ...draft.meta };
  points.value = [...draft.points];
  currentPosition.value = draft.points[draft.points.length - 1] ?? null;
  lastFlushedIndex = draft.points.length;
  resetRunningStats(draft.points);
  pendingDraft.value = null;
  error.value = null;
  lastFixAt.value = null;
  stallEpisodeActive = false;
  lastWatcherRestartAt = 0;
  watchdogRestartInFlight = false;
  isRecording.value = true;
  isPaused.value = false;

  if (!(await startGpsWatchers())) {
    isRecording.value = false;
    isPaused.value = false;
    recordingFlightId = null;
    draftMeta = null;
    pendingDraft.value = draft.meta;
    return false;
  }

  startCheckpointTimer();
  clock.start(draft.meta.startTime);
  startTicker();
  autoDetector.beginRecording(draft.meta.startTime);
  autoStartPending.value = false;
  recorderLog('recording resumed from draft', { pointCount: draft.points.length });
  return true;
}

async function finalizeDraft(): Promise<Flight | null> {
  const draft = await loadRecordingDraft();
  if (!draft) {
    pendingDraft.value = null;
    return null;
  }

  if (draft.points.length < 2) {
    error.value = 'Not enough GPS points recorded. Try recording for longer.';
    await clearRecordingDraft();
    pendingDraft.value = null;
    return null;
  }

  points.value = draft.points;
  resetRunningStats(draft.points);
  const flight = buildFlightFromPoints(draft.meta.id, pointsDurationMs());
  points.value = [];
  currentPosition.value = null;
  runningMaxAltitude = 0;
  runningTotalDistanceKm = 0;
  await clearRecordingDraft();
  pendingDraft.value = null;
  return flight;
}

async function discardDraft(): Promise<void> {
  await clearRecordingDraft();
  pendingDraft.value = null;
}

function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

function toDeg(rad: number): number {
  return (rad * 180) / Math.PI;
}

function normalizeDegrees(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

function shortestAngleDiffDegrees(fromDeg: number, toDeg: number): number {
  return ((toDeg - fromDeg + 540) % 360) - 180;
}

function calculateBearing(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const lat1Rad = toRad(lat1);
  const lat2Rad = toRad(lat2);
  const deltaLonRad = toRad(lon2 - lon1);
  const y = Math.sin(deltaLonRad) * Math.cos(lat2Rad);
  const x =
    (Math.cos(lat1Rad) * Math.sin(lat2Rad)) -
    (Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(deltaLonRad));
  const initial = toDeg(Math.atan2(y, x));
  return normalizeDegrees(initial);
}
